import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  runTransaction,
  updateDoc,
  where,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import { wrapSubscriptionError } from './subscriptionError';
import { mapUserAccount, UserAccount } from '../types/userAccount';

const USER_ACCOUNTS = 'user_accounts';
const USER_IN_QUERY_LIMIT = 30;
const ACCOUNT_SUBSCRIPTION_RETRY_BASE_MS = 1000;
const ACCOUNT_SUBSCRIPTION_RETRY_MAX_MS = 10000;

async function getUserAccountsByField(field: 'email', values: string[]): Promise<UserAccount[]> {
  const uniqueValues = Array.from(new Set(values.filter(Boolean)));
  const rows: UserAccount[] = [];
  for (let offset = 0; offset < uniqueValues.length; offset += USER_IN_QUERY_LIMIT) {
    const chunk = uniqueValues.slice(offset, offset + USER_IN_QUERY_LIMIT);
    const snap = await getDocs(query(collection(db, USER_ACCOUNTS), where(field, 'in', chunk)));
    rows.push(...snap.docs.map((item) => mapUserAccount(item.id, item.data())));
  }
  return Array.from(new Map(rows.map((row) => [row.id, row])).values());
}

export interface UserAccountSnapshotMetadata {
  fromCache: boolean;
  hasPendingWrites: boolean;
}

/**
 * The current user's account is found by querying `user_accounts` for the doc
 * whose `firebaseId` matches the Firebase Auth uid (docs are NOT keyed by uid).
 * Snapshot metadata distinguishes a cached empty result from an authoritative
 * missing-account result; failed listeners retry with bounded backoff.
 */
export function subscribeToUserAccountByFirebaseId(
  uid: string,
  onNext: (account: UserAccount | null, metadata: UserAccountSnapshotMetadata) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(collection(db, USER_ACCOUNTS), where('firebaseId', '==', uid), limit(1));
  let stopped = false;
  let unsubscribe: Unsubscribe | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let retryAttempt = 0;

  function scheduleRetry() {
    if (stopped || retryTimer !== null) return;
    const delay = Math.min(
      ACCOUNT_SUBSCRIPTION_RETRY_BASE_MS * 2 ** retryAttempt,
      ACCOUNT_SUBSCRIPTION_RETRY_MAX_MS,
    );
    retryAttempt += 1;
    retryTimer = setTimeout(() => {
      retryTimer = null;
      subscribe();
    }, delay);
  }

  function subscribe() {
    if (stopped) return;
    unsubscribe = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => {
        if (stopped) return;
        retryAttempt = 0;
        const doc = snap.docs[0];
        onNext(
          doc ? mapUserAccount(doc.id, doc.data()) : null,
          {
            fromCache: snap.metadata.fromCache,
            hasPendingWrites: snap.metadata.hasPendingWrites,
          },
        );
      },
      wrapSubscriptionError('subscribeToUserAccountByFirebaseId', (err) => {
        unsubscribe = null;
        onError?.(err);
        scheduleRetry();
      }),
    );
  }

  subscribe();
  return () => {
    stopped = true;
    if (retryTimer !== null) clearTimeout(retryTimer);
    retryTimer = null;
    unsubscribe?.();
    unsubscribe = null;
  };
}

export async function getUserAccountByFirebaseId(uid: string): Promise<UserAccount | null> {
  const q = query(collection(db, USER_ACCOUNTS), where('firebaseId', '==', uid), limit(1));
  const snap = await getDocs(q);
  const doc = snap.docs[0];
  return doc ? mapUserAccount(doc.id, doc.data()) : null;
}

export async function getUserAccountByEmail(email: string): Promise<UserAccount | null> {
  const q = query(collection(db, USER_ACCOUNTS), where('email', '==', email), limit(1));
  const snap = await getDocs(q);
  const doc = snap.docs[0];
  return doc ? mapUserAccount(doc.id, doc.data()) : null;
}

/**
 * Direct doc-id read, matching aligned's `FirestoreApiService.getUserAccountById`.
 * Invalid Firestore doc ids are rejected before constructing the doc ref.
 */
export async function getUserAccountById(id: string): Promise<UserAccount | null> {
  if (!id || id.includes('/') || new TextEncoder().encode(id).length > 1500) return null;
  const snap = await getDoc(doc(db, USER_ACCOUNTS, id));
  return snap.exists() ? mapUserAccount(snap.id, snap.data()) : null;
}

/**
 * Port of aligned's `getUserAccountByEmailOrId`: email query first for email
 * search text, direct doc lookup otherwise. Admin search lowercases emails but
 * preserves case-sensitive ids.
 */
export async function getUserAccountByEmailOrId(searchText: string): Promise<UserAccount | null> {
  return searchText.includes('@') ? getUserAccountByEmail(searchText.toLowerCase()) : getUserAccountById(searchText);
}

/** Ported from aligned's `getAllUserAccounts`, used by admin name search. */
export async function getAllUserAccounts(): Promise<UserAccount[]> {
  const snap = await getDocs(collection(db, USER_ACCOUNTS));
  return snap.docs.map((d) => mapUserAccount(d.id, d.data()));
}

/** Account identity is the document path; never query the denormalized `id` field. */
export async function getUserAccountsByIds(ids: string[]): Promise<UserAccount[]> {
  const uniqueIds = Array.from(
    new Set(
      ids.filter(
        (id) => id && !id.includes('/') && new TextEncoder().encode(id).length <= 1500,
      ),
    ),
  );
  // Keep this batched: 30 accounts per query, all queries at once. Campaign
  // detail pages can pass more than 1,000 experts; one getDoc per expert
  // (thousands of single reads) is what exhausts Firestore's outstanding
  // request budget, not a few dozen batched queries.
  const chunks: string[][] = [];
  for (let offset = 0; offset < uniqueIds.length; offset += USER_IN_QUERY_LIMIT) {
    chunks.push(uniqueIds.slice(offset, offset + USER_IN_QUERY_LIMIT));
  }
  const results = await Promise.all(chunks.map(async (chunk) => {
    const snap = await getDocs(query(collection(db, USER_ACCOUNTS), where(documentId(), 'in', chunk)));
    return snap.docs.map((item) => mapUserAccount(item.id, item.data()));
  }));
  // Chunk order, as when the queries ran one at a time.
  return results.flat();
}

/** Aligned's chunked `getUserAccountListByEmails` query (`email in` groups of 30). */
export function getUserAccountsByEmails(emails: string[]): Promise<UserAccount[]> {
  return getUserAccountsByField('email', emails.map((email) => email.toLowerCase()));
}

/**
 * Resolves pasted identifiers that may mix emails and user account IDs (a
 * spreadsheet column, an exported list). Anything with `@` is an email.
 * `missing` keeps the caller's original spelling so it can be shown back.
 */
export async function resolveUserIdentifiers(
  inputs: string[],
): Promise<{ found: UserAccount[]; missing: string[] }> {
  const normalized = Array.from(new Set(inputs.map((value) => value.trim()).filter(Boolean)));
  const emails = normalized.filter((value) => value.includes('@'));
  const ids = normalized.filter((value) => !value.includes('@'));
  const [byEmail, byId] = await Promise.all([
    emails.length > 0 ? getUserAccountsByEmails(emails) : Promise.resolve([]),
    ids.length > 0 ? getUserAccountsByIds(ids) : Promise.resolve([]),
  ]);
  const foundEmails = new Set(byEmail.map((user) => user.email.toLowerCase()));
  const foundIds = new Set(byId.map((user) => user.id));
  const found = Array.from(new Map([...byId, ...byEmail].map((user) => [user.id, user])).values());
  const missing = normalized.filter((value) =>
    value.includes('@') ? !foundEmails.has(value.toLowerCase()) : !foundIds.has(value));
  return { found, missing };
}

/** Ported from aligned's `getAllNonRobotExperts`; the robot filter is commented out upstream, leaving only the demo-account exclusion. */
export async function getAllNonRobotExperts(): Promise<UserAccount[]> {
  const users = await getAllUserAccounts();
  return users.filter((user) => user.id !== 'demoUserAccount');
}

/** Ported from aligned's `getUsersForExpertPool`: approved members whose `assignedExpertPoolIds` contains the pool id. */
export async function getUsersForExpertPool(expertPoolId: string): Promise<UserAccount[]> {
  const q = query(collection(db, USER_ACCOUNTS), where('assignedExpertPoolIds', 'array-contains', expertPoolId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => mapUserAccount(d.id, d.data()));
}

/** Aligned's `getUserAccountListByExpertPoolIdList`: one `array-contains-any`
 * query per Firestore-sized chunk, with duplicate accounts removed. */
export async function getUsersForExpertPools(expertPoolIds: string[]): Promise<UserAccount[]> {
  const uniqueExpertPoolIds = Array.from(new Set(expertPoolIds.filter(Boolean)));
  const rows: UserAccount[] = [];
  for (let offset = 0; offset < uniqueExpertPoolIds.length; offset += USER_IN_QUERY_LIMIT) {
    const chunk = uniqueExpertPoolIds.slice(offset, offset + USER_IN_QUERY_LIMIT);
    const snap = await getDocs(query(collection(db, USER_ACCOUNTS), where('assignedExpertPoolIds', 'array-contains-any', chunk)));
    rows.push(...snap.docs.map((item) => mapUserAccount(item.id, item.data())));
  }
  return Array.from(new Map(rows.map((row) => [row.id, row])).values());
}

/**
 * Partial field update on the public `user_accounts` doc (Settings screens'
 * `firstNamePreferred`/`currentHourlyWageBid`/etc. writes). Deliberately a
 * targeted Firestore `updateDoc` of only the given fields, NOT a translation
 * of aligned's `updateCurrentUserAccount` (which re-serializes and writes the
 * *entire* in-memory `UserAccount` back) — our `UserAccount` type models only
 * a subset of the ~65-field doc, so a full-document write from it would wipe
 * every field this port hasn't modelled yet.
 */
export function updateUserAccountFields(
  userAccountId: string,
  fields: Partial<UserAccount>,
): Promise<void> {
  return updateDoc(doc(db, USER_ACCOUNTS, userAccountId), fields);
}

/**
 * Swap pools on an account without losing concurrent changes. Reads
 * `assignedExpertPoolIds` inside a transaction, drops `remove`, appends `add`,
 * and writes it back together with any `extraFields`. Never build the new list
 * from a `UserAccount` loaded earlier: a pool approved in the meantime (same
 * reviewer, another tab, the matcher) would be silently overwritten. Pure adds
 * and removes can use `arrayUnion` / `arrayRemove` instead.
 */
export function replaceUserExpertPools(
  userAccountId: string,
  change: { remove?: readonly string[]; add?: readonly string[] },
  extraFields: Partial<UserAccount> = {},
): Promise<string[]> {
  const ref = doc(db, USER_ACCOUNTS, userAccountId);
  return runTransaction(db, async (transaction) => {
    const snap = await transaction.get(ref);
    if (!snap.exists()) throw new Error('This expert account no longer exists.');
    const raw = snap.data().assignedExpertPoolIds;
    const current = Array.isArray(raw) ? raw.filter((id: unknown): id is string => typeof id === 'string') : [];
    const remove = new Set(change.remove ?? []);
    const next = current.filter((id) => !remove.has(id));
    for (const id of change.add ?? []) if (!next.includes(id)) next.push(id);
    transaction.update(ref, { ...extraFields, assignedExpertPoolIds: next });
    return next;
  });
}

/**
 * Every admin account that is not deprecated. Production admin docs usually
 * have no `deprecatedOn` field at all, and Firestore's `== null` filter skips
 * documents that are missing the field, so deprecation is checked client-side.
 */
export async function getActiveAdminUsers(): Promise<UserAccount[]> {
  const snap = await getDocs(query(collection(db, USER_ACCOUNTS), where('isAdmin', '==', true)));
  return snap.docs
    .filter((d) => d.data().deprecatedOn == null)
    .map((d) => mapUserAccount(d.id, d.data()));
}

/**
 * Ported from `ManagementService.getAdminUsers`: every non-deprecated admin account.
 * Note: `deprecatedOn == null` excludes docs that lack the field, which is most
 * production admins; see `getActiveAdminUsers`.
 */
export async function getAdminUsers(): Promise<UserAccount[]> {
  const q = query(collection(db, USER_ACCOUNTS), where('isAdmin', '==', true), where('deprecatedOn', '==', null));
  const snap = await getDocs(q);
  return snap.docs.map((d) => mapUserAccount(d.id, d.data()));
}
